import { CROWNS_LEVELS } from './crownsPuzzleLevels';
import { DAILY_SIZES } from './crownsDailyIndex';

// Puzzles are the Crowns app's level files: the dailies (`daily-<n>`) from
// public/crowns-daily, the rest shipped with the Crowns web build.
export const levelUrl = (id) => (String(id).startsWith('daily-')
  ? `/crowns-daily/${String(id).slice(6)}.json`
  : `/crowns/assets/assets/levels/${id}.json`);

// Region colours: the Crowns app's default theme (ids 1..20).
export const REGION_COLORS = {
  1: '#DCA7A9', 2: '#F1D3C9', 3: '#F7EBE5', 4: '#F0A77F', 5: '#5F8081',
  6: '#E7BF74', 7: '#9BB096', 8: '#F0E76A', 9: '#D8E2C4', 10: '#D88C8C',
  11: '#97CED8', 12: '#83AAAF', 13: '#B797D6', 14: '#B3AA94', 15: '#A3B4EE',
  16: '#D1F0F0', 17: '#7E7792', 18: '#C0B7AF', 19: '#808000', 20: '#FF00FF',
};

export function isDark(hex) {
  const v = parseInt(hex.slice(1), 16);
  const r = (v >> 16) & 255; const g = (v >> 8) & 255; const b = v & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
}

// ---- Daily schedule -------------------------------------------------------
// The Crowns app's daily, so web and app players solve the same puzzle each day
// and share a leaderboard (crowns lib/utils/daily_cycle.dart). Puzzle #1 was
// 2025-03-21; from #571 (2026-10-12) the app plays its second set of 365
// dailies in order (public/crowns-daily, scripts/generate-crowns-daily.mjs).
// The puzzle changes at local midnight.
const DAY_MS = 86400000;
const APP_EPOCH_UTC = Date.UTC(2025, 2, 21);
const CYCLE2_FIRST = 571;
const CYCLE2_LENGTH = DAILY_SIZES.length;

export function localDateKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const keyToUtc = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

export function previousDateKey(key) {
  const t = new Date(keyToUtc(key) - DAY_MS);
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}

// `board`: the app's leaderboard number for the day (day of the year, 1..366).
export function dailyPuzzle(key) {
  const t = keyToUtc(key);
  const number = Math.round((t - APP_EPOCH_UTC) / DAY_MS) + 1;
  const index = ((((number - CYCLE2_FIRST) % CYCLE2_LENGTH) + CYCLE2_LENGTH) % CYCLE2_LENGTH) + 1;
  const board = Math.round((t - Date.UTC(Number(key.slice(0, 4)), 0, 1)) / DAY_MS) + 1;
  return { key, number, size: DAILY_SIZES[index - 1], id: `daily-${index}`, board };
}

// ---- Unlimited ------------------------------------------------------------
export const DIFFICULTIES = [
  { key: 'easy', label: 'Easy', sizes: [5, 6] },
  { key: 'medium', label: 'Medium', sizes: [7, 8] },
  { key: 'hard', label: 'Hard', sizes: [9, 10] },
  { key: 'expert', label: 'Expert', sizes: [11, 12] },
];

export function randomPuzzle(difficultyKey, avoidIds = []) {
  const diff = DIFFICULTIES.find((d) => d.key === difficultyKey) || DIFFICULTIES[1];
  const size = diff.sizes[Math.floor(Math.random() * diff.sizes.length)];
  const pool = CROWNS_LEVELS[size];
  let id = pool[Math.floor(Math.random() * pool.length)];
  for (let i = 0; i < 5 && avoidIds.includes(id); i += 1) {
    id = pool[Math.floor(Math.random() * pool.length)];
  }
  return { size, id };
}

// ---- Board ----------------------------------------------------------------
// Cell states: 0 empty, 1 marked (X), 2 crown.
export const EMPTY = 0;
export const MARK = 1;
export const CROWN = 2;

export const emptyBoard = (n) => new Array(n * n).fill(EMPTY);

export function crownsOf(board, n) {
  const out = [];
  board.forEach((v, i) => { if (v === CROWN) out.push([Math.floor(i / n), i % n]); });
  return out;
}

// Indices of crowns that break a rule (same row, column or region, or touching).
export function conflicts(board, n, regions) {
  const qs = crownsOf(board, n);
  const bad = new Set();
  for (let a = 0; a < qs.length; a += 1) {
    for (let b = a + 1; b < qs.length; b += 1) {
      const [r1, c1] = qs[a]; const [r2, c2] = qs[b];
      if (r1 === r2 || c1 === c2 || regions[r1][c1] === regions[r2][c2]
        || (Math.abs(r1 - r2) <= 1 && Math.abs(c1 - c2) <= 1)) {
        bad.add(r1 * n + c1); bad.add(r2 * n + c2);
      }
    }
  }
  return bad;
}

export function isSolved(board, n, regions) {
  return crownsOf(board, n).length === n && conflicts(board, n, regions).size === 0;
}

// Mark every empty cell a crown at (r, c) rules out.
export function autoMark(board, n, regions, r, c) {
  const next = board.slice();
  for (let rr = 0; rr < n; rr += 1) {
    for (let cc = 0; cc < n; cc += 1) {
      if (rr === r && cc === c) continue;
      const i = rr * n + cc;
      if (next[i] !== EMPTY) continue;
      if (rr === r || cc === c || regions[rr][cc] === regions[r][c]
        || (Math.abs(rr - r) <= 1 && Math.abs(cc - c) <= 1)) {
        next[i] = MARK;
      }
    }
  }
  return next;
}

// One hint: remove a wrong crown, else clear a wrong X, else place a crown.
// Returns { board, cell, message } or null if nothing to do.
export function hint(board, n, solution) {
  const sol = solution.split(',').map(Number);
  for (let i = 0; i < board.length; i += 1) {
    const r = Math.floor(i / n); const c = i % n;
    if (board[i] === CROWN && sol[r] !== c) {
      const next = board.slice(); next[i] = EMPTY;
      return { board: next, cell: i, message: 'That crown was in the wrong place, so it has been removed.' };
    }
  }
  for (let r = 0; r < n; r += 1) {
    const i = r * n + sol[r];
    if (board[i] === MARK) {
      const next = board.slice(); next[i] = EMPTY;
      return { board: next, cell: i, message: 'One of your X marks covers a crown. It has been cleared.' };
    }
  }
  for (let r = 0; r < n; r += 1) {
    const i = r * n + sol[r];
    if (board[i] !== CROWN) {
      const next = board.slice(); next[i] = CROWN;
      return { board: next, cell: i, message: 'A crown has been placed for you.' };
    }
  }
  return null;
}

export function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
